import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, RefreshCw, X, EyeOff } from "lucide-react";
import { API } from "@/lib/auth";

/** Full CRUD editor for shop products (physical + digital). Mirrors CourseEditor. */
export default function ProductEditor() {
  const [products, setProducts] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/products`);
      setProducts(r.data || []);
      if (!selectedSlug && r.data?.length) setSelectedSlug(r.data[0].slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load products");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = useMemo(
    () => products.find((p) => p.slug === selectedSlug) || null,
    [products, selectedSlug],
  );

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/products/pricing/sync-from-stripe`);
      toast.success(`Synced. ${r.data.products_updated} product(s) updated from Stripe.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-12" data-testid="admin-products-panel">
      <div className="lg:col-span-4 space-y-3">
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
          <div className="flex items-center justify-between">
            <div className="overline">Products</div>
            <button onClick={() => setCreating(true)} data-testid="product-new" className="btn-pill btn-outline !text-xs !py-1.5">
              <Plus size={12} /> New
            </button>
          </div>
          <div className="mt-4 space-y-1.5 max-h-[calc(100vh-350px)] overflow-y-auto">
            {products.map((p) => (
              <button
                key={p.slug}
                onClick={() => { setSelectedSlug(p.slug); setCreating(false); }}
                data-testid={`product-item-${p.slug}`}
                className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${
                  selectedSlug === p.slug && !creating
                    ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                    : "hover:bg-blush/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{p.name}</span>
                  {p.hidden && <EyeOff size={12} className="opacity-60 shrink-0" />}
                </div>
                <div className="text-[10px] mt-0.5 opacity-60 tracking-widest uppercase truncate">
                  £{(p.amount_pence / 100).toFixed(2)} · {p.category}
                  {p.stock !== null && p.stock !== undefined && (
                    <span className={p.stock === 0 ? "ml-1 text-[hsl(var(--burgundy))]" : p.stock <= 3 ? "ml-1 text-[hsl(var(--gold))]" : ""}>
                      {" · "}{p.stock === 0 ? "SOLD OUT" : `${p.stock} in stock`}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={syncStripe}
          disabled={syncing}
          data-testid="product-sync-stripe"
          className="btn-pill btn-outline w-full !text-xs disabled:opacity-60"
        >
          <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : "Sync prices from Stripe"}
        </button>
      </div>

      <div className="lg:col-span-8">
        {creating ? (
          <ProductCreateForm onCreated={(slug) => { setCreating(false); setSelectedSlug(slug); load(); }} onCancel={() => setCreating(false)} />
        ) : selected ? (
          <ProductForm key={selected.slug} product={selected} onSaved={load} />
        ) : (
          <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
            Pick a product to edit.
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCreateForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    slug: "", name: "", category: "Aftercare", tag: "",
    shortDesc: "", longDesc: "", digital: false,
    amount_pence: 2500, sort_order: 100, hidden: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9-]+$/.test(form.slug)) return toast.error("Slug must be lowercase letters/numbers/hyphens");
    if (!window.confirm(`Create a new Stripe Product + Price at £${(form.amount_pence / 100).toFixed(2)}? This is LIVE.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/products`, {
        ...form,
        images: [],
        amount_pence: Number(form.amount_pence),
      });
      toast.success("Product created — Stripe product + price live.");
      onCreated(form.slug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create product");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4" data-testid="product-create-form">
      <div className="flex items-center justify-between">
        <div className="overline">New product</div>
        <button type="button" onClick={onCancel} className="text-xs text-[hsl(var(--onyx)/0.55)] hover:text-[hsl(var(--onyx))]">Cancel</button>
      </div>
      <LabeledInput label="Slug (URL-safe)" required value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase() })} testid="pnew-slug" placeholder="silk-scrunchie-set" />
      <LabeledInput label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="pnew-name" />
      <div className="grid gap-3 sm:grid-cols-2">
        <LabeledInput label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
        <LabeledInput label="Tag (optional)" value={form.tag} onChange={(v) => setForm({ ...form, tag: v })} placeholder="New / Bestseller" />
      </div>
      <LabeledTextarea label="Short description" value={form.shortDesc} onChange={(v) => setForm({ ...form, shortDesc: v })} rows={2} />
      <LabeledTextarea label="Long description" value={form.longDesc} onChange={(v) => setForm({ ...form, longDesc: v })} rows={4} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Amount (pence)</label>
          <input type="number" value={form.amount_pence} onChange={(e) => setForm({ ...form, amount_pence: Number(e.target.value) })} data-testid="pnew-amount" className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Sort order</label>
          <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm" />
        </div>
      </div>
      <div className="flex gap-4 text-xs">
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.digital} onChange={(e) => setForm({ ...form, digital: e.target.checked })} /> Digital product</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} /> Hidden</label>
      </div>
      <button type="submit" disabled={busy} data-testid="product-create-submit" className="btn-pill btn-primary disabled:opacity-60">
        {busy ? "Creating in Stripe…" : "Create product"}
      </button>
    </form>
  );
}

function ProductForm({ product, onSaved }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState(product.amount_pence);
  useEffect(() => { setForm(product); setPriceInput(product.amount_pence); }, [product]);
  const dirty = JSON.stringify(form) !== JSON.stringify(product);
  const priceDirty = Number(priceInput) !== product.amount_pence;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, category: form.category, tag: form.tag,
        shortDesc: form.shortDesc, longDesc: form.longDesc, image: form.image,
        images: form.images || [], digital: !!form.digital,
        lengths: form.lengths, colours: form.colours,
        stock: form.stock === "" || form.stock === null ? null : Number(form.stock),
        hidden: !!form.hidden, sort_order: Number(form.sort_order) || 100,
      };
      await axios.patch(`${API}/admin/products/${product.slug}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const savePrice = async () => {
    if (!priceDirty) return;
    const newAmount = Number(priceInput);
    if (!window.confirm(`Change price to £${(newAmount / 100).toFixed(2)}? A new Stripe Price will be created; the old one archived. Affects LIVE checkouts.`)) return;
    setSavingPrice(true);
    try {
      await axios.patch(`${API}/admin/products/${product.slug}/price`, { amount_pence: newAmount });
      toast.success("Price updated in Stripe");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Price update failed");
    } finally { setSavingPrice(false); }
  };

  const uploadImage = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/products/${product.slug}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: r.data.image, images: [...(f.images || []), r.data.image] }));
      toast.success("Image uploaded");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${product.name}"? Its Stripe price will be archived.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.slug}`);
      toast.success("Product removed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid={`product-edit-${product.slug}`}>
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Editing</div>
          <h3 className="font-display text-2xl mt-1 tracking-tight">{product.name}</h3>
          <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-1">{product.slug} · lookup_key: {product.lookup_key}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving || !dirty} data-testid="product-save" className="btn-pill btn-primary disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
          <button onClick={remove} data-testid="product-delete" className="btn-pill btn-outline">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Cover image</div>
        <div className="grid gap-4 sm:grid-cols-[240px_1fr] items-start">
          <div className="aspect-square rounded-2xl overflow-hidden bg-blush/40 border border-[hsl(var(--onyx)/0.08)]">
            {form.image ? <img src={form.image} alt={form.name} className="w-full h-full object-cover" /> : (
              <div className="w-full h-full flex items-center justify-center text-xs text-[hsl(var(--onyx)/0.45)]">No image yet</div>
            )}
          </div>
          <div className="space-y-3">
            <label htmlFor={`prodfile-${product.slug}`} data-testid="product-image-uploader"
              className={`block w-full text-center py-6 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${uploading ? "border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.35)]" : "border-[hsl(var(--onyx)/0.2)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx))] hover:text-[hsl(var(--onyx))]"}`}>
              {uploading ? "Uploading…" : <><Upload size={16} className="inline mr-2" /> Upload image</>}
              <div className="text-[11px] mt-1 text-[hsl(var(--onyx)/0.5)]">JPG · PNG · WebP · HEIC · up to 12 MB</div>
            </label>
            <input id={`prodfile-${product.slug}`} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" disabled={uploading}
              onChange={(e) => { uploadImage(e.target.files); e.target.value = ""; }} className="hidden" />
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Basics</div>
        <LabeledInput label="Name" value={form.name || ""} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledInput label="Category" value={form.category || ""} onChange={(v) => setForm({ ...form, category: v })} />
          <LabeledInput label="Tag" value={form.tag || ""} onChange={(v) => setForm({ ...form, tag: v })} placeholder="Bestseller / New" />
        </div>
        <LabeledTextarea label="Short description (card blurb)" value={form.shortDesc || ""} onChange={(v) => setForm({ ...form, shortDesc: v })} rows={2} testid="edit-shortdesc" />
        <LabeledTextarea label="Long description (product page)" value={form.longDesc || ""} onChange={(v) => setForm({ ...form, longDesc: v })} rows={5} testid="edit-longdesc" />
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!form.digital} onChange={(e) => setForm({ ...form, digital: e.target.checked })} /> Digital</label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} /> Hidden</label>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Sort order</label>
            <input type="number" value={form.sort_order ?? 100} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} className="w-full h-9 mt-1 rounded-xl border border-[hsl(var(--onyx)/0.15)] px-3 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)] flex items-center justify-between">
            <span>Stock level</span>
            <span className="text-[9px] normal-case tracking-normal text-[hsl(var(--onyx)/0.45)]">
              Leave empty for unlimited stock
            </span>
          </label>
          <input
            type="number"
            min="0"
            placeholder="Unlimited"
            value={form.stock ?? ""}
            onChange={(e) => setForm({ ...form, stock: e.target.value === "" ? null : Math.max(0, Number(e.target.value)) })}
            data-testid="edit-stock"
            className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
          />
          {form.stock !== null && form.stock !== undefined && form.stock !== "" && (
            <div className={`mt-2 text-xs ${Number(form.stock) === 0 ? "text-[hsl(var(--burgundy))]" : Number(form.stock) <= 3 ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--onyx)/0.6)]"}`}>
              {Number(form.stock) === 0
                ? "Out of stock — 'Sold Out' shown to shoppers; Add-to-Cart disabled."
                : Number(form.stock) <= 3
                ? `Low stock — 'Only ${form.stock} left!' badge will appear on the shop page.`
                : `${form.stock} in stock`}
            </div>
          )}
        </div>
      </div>

      <VariantEditor title="Lengths (leave empty if not applicable)" items={form.lengths || []} onChange={(v) => setForm({ ...form, lengths: v })} placeholder='e.g. 18"' testidPrefix="edit-lengths" />
      <VariantEditor title="Colours (leave empty if not applicable)" items={form.colours || []} onChange={(v) => setForm({ ...form, colours: v })} placeholder="e.g. Warm Blonde" testidPrefix="edit-colours" />

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-3" data-testid="product-pricing">
        <div className="overline">Price</div>
        <p className="text-xs text-[hsl(var(--onyx)/0.6)] max-w-md">
          Changes here push straight to <strong>live Stripe</strong>. A new Price is created and the old one archived.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-lg">£</span>
            <input type="number" value={priceInput / 100} onChange={(e) => setPriceInput(Math.round(Number(e.target.value) * 100))}
              data-testid="product-price-input"
              className="w-28 h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-3 text-sm text-right" />
          </div>
          <button onClick={savePrice} disabled={!priceDirty || savingPrice} data-testid="product-price-save" className="btn-pill btn-primary disabled:opacity-50">
            {savingPrice ? "Updating Stripe…" : "Update price"}
          </button>
          <div className="text-xs text-[hsl(var(--onyx)/0.5)]">Current: £{(product.amount_pence / 100).toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}

function VariantEditor({ title, items, onChange, placeholder, testidPrefix }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...(items || []), draft.trim()]);
    setDraft("");
  };
  const remove = (i) => onChange((items || []).filter((_, j) => j !== i));
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-3" data-testid={`${testidPrefix}-panel`}>
      <div className="overline">{title}</div>
      <div className="flex flex-wrap gap-2">
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blush/50 text-sm">
            {it}
            <button type="button" onClick={() => remove(i)} data-testid={`${testidPrefix}-remove-${i}`} className="opacity-60 hover:opacity-100">
              <X size={12} />
            </button>
          </span>
        ))}
        {(items || []).length === 0 && <span className="text-xs text-[hsl(var(--onyx)/0.45)] italic">Nothing added</span>}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`${testidPrefix}-input`}
          className="flex-1 h-10 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
        <button type="button" onClick={add} data-testid={`${testidPrefix}-add`} className="btn-pill btn-outline !text-xs"><Plus size={12} /> Add</button>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder, testid, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testid}
        className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
    </div>
  );
}
function LabeledTextarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <textarea value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-y" />
    </div>
  );
}
